import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Chip,
    Snackbar,
    ThemeProvider,
    Typography,
} from '@mui/material';
import {
    deleteBrand,
    deleteProduct,
    getBrandById,
    listProducts,
    reorderProducts,
} from '../../api';
import { reorderList } from '../../utils/reorderList';
import { toImgSrcKey } from '../../utils/cdn';
import { blackIconTheme } from '../../theme/blackIconTheme';
import BrandProductsGrid from '../products/components/BrandProductsGrid';
import styles from './BrandDetailPage.module.scss';

async function listAllBrandProducts(brandId) {
    const pageSize = 200;
    let page = 1;
    let total = Number.POSITIVE_INFINITY;
    const items = [];

    while (items.length < total) {
        const response = await listProducts({
            page,
            limit: pageSize,
            brandId,
            sort: 'order',
            dir: 'asc',
        });

        const chunk = Array.isArray(response?.items) ? response.items : [];
        if (!chunk.length) break;

        items.push(...chunk);
        total = Number(response?.total ?? items.length);

        if (chunk.length < pageSize) break;
        page += 1;
    }

    return items;
}

export default function BrandDetailPage() {
    const { id: routeId } = useParams();
    const id = useMemo(() => {
        const raw = String(routeId ?? '');
        if (!raw) return '';
        try {
            return decodeURIComponent(raw);
        } catch {
            return raw;
        }
    }, [routeId]);
    const navigate = useNavigate();

    const [brand, setBrand] = useState(null);
    const [items, setItems] = useState([]);
    const [selectedIds, setSelectedIds] = useState(new Set());

    const [loading, setLoading] = useState(true);
    const [bulkBusy, setBulkBusy] = useState(false);
    const [err, setErr] = useState('');
    const [notice, setNotice] = useState({ open: false, message: '', severity: 'success' });

    const [autosave, setAutosave] = useState('idle');

    const saveInFlightRef = useRef(false);
    const queuedIdsRef = useRef(null);
    const rollbackItemsRef = useRef([]);
    const autosaveTimeoutRef = useRef(null);

    const productCount = items.length;
    const selectedCount = selectedIds.size;

    const flushSaveQueue = useCallback(async () => {
        if (saveInFlightRef.current || !queuedIdsRef.current || !id) return;

        saveInFlightRef.current = true;
        const idsToSave = queuedIdsRef.current;
        queuedIdsRef.current = null;

        try {
            await reorderProducts(id, idsToSave);
            setAutosave('saved');

            if (autosaveTimeoutRef.current) clearTimeout(autosaveTimeoutRef.current);
            autosaveTimeoutRef.current = setTimeout(() => setAutosave('idle'), 1300);
        } catch (e) {
            setAutosave('error');
            setErr(e?.message || 'Failed to save product order');
            setItems(rollbackItemsRef.current);
            setNotice({ open: true, message: e?.message || 'Failed to save order', severity: 'error' });
        } finally {
            saveInFlightRef.current = false;
            if (queuedIdsRef.current) {
                setAutosave('saving');
                flushSaveQueue();
            }
        }
    }, [id]);

    const queueOrderSave = useCallback((nextItems, previousItems) => {
        rollbackItemsRef.current = previousItems;
        queuedIdsRef.current = nextItems.map((item) => item.id);
        setAutosave('saving');
        flushSaveQueue();
    }, [flushSaveQueue]);

    const load = useCallback(async () => {
        if (!id) {
            setLoading(false);
            setBrand(null);
            setItems([]);
            setErr('Invalid brandId');
            return;
        }

        try {
            setLoading(true);
            setErr('');

            const [brandData, productData] = await Promise.all([
                getBrandById(id),
                listAllBrandProducts(id),
            ]);

            if (!brandData?.id) {
                setBrand(null);
                setItems([]);
                setErr('Brand not found');
                setSelectedIds(new Set());
                return;
            }

            setBrand(brandData);
            setItems(Array.isArray(productData) ? productData : []);
            setSelectedIds(new Set());
        } catch (e) {
            if (e?.status === 404) setErr('Brand not found');
            else if (e?.status === 400) setErr('Invalid brandId');
            else setErr(e?.message || 'Failed to load brand');
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        load();
        return () => {
            if (autosaveTimeoutRef.current) clearTimeout(autosaveTimeoutRef.current);
        };
    }, [load]);

    const autosaveLabel = useMemo(() => {
        if (autosave === 'saving') return 'Autosaving order...';
        if (autosave === 'saved') return 'Order saved';
        if (autosave === 'error') return 'Autosave failed';
        return 'Order applies inside this brand only';
    }, [autosave]);

    async function onDeleteBrand() {
        if (productCount > 0) {
            setErr('Delete or move products before removing the brand.');
            return;
        }

        if (!window.confirm('Delete this brand?')) return;

        try {
            await deleteBrand(id);
            navigate('/brands', { replace: true });
        } catch (e) {
            setErr(e?.message || 'Failed to delete brand');
        }
    }

    async function onDeleteProduct(productId) {
        if (!window.confirm('Delete product?')) return;

        try {
            await deleteProduct(productId);
            setItems((prev) => prev.filter((item) => item.id !== productId));
            setSelectedIds((prev) => {
                const next = new Set(prev);
                next.delete(productId);
                return next;
            });
            setNotice({ open: true, message: 'Product deleted', severity: 'success' });
        } catch (e) {
            setErr(e?.message || 'Failed to delete product');
            setNotice({ open: true, message: e?.message || 'Failed to delete product', severity: 'error' });
        }
    }

    async function onBulkDeleteSelected() {
        const ids = Array.from(selectedIds);
        if (!ids.length) return;

        if (!window.confirm(`Delete ${ids.length} selected product(s)?`)) return;

        try {
            setBulkBusy(true);
            setErr('');

            const results = await Promise.allSettled(ids.map((productId) => deleteProduct(productId)));
            const failed = results.filter((r) => r.status === 'rejected');

            if (failed.length) {
                setErr(`Deleted ${ids.length - failed.length}/${ids.length}. Some products failed to delete.`);
            } else {
                setNotice({ open: true, message: `Deleted ${ids.length} products`, severity: 'success' });
            }

            await load();
        } finally {
            setBulkBusy(false);
        }
    }

    function toggleSelect(productId, checked) {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (checked) next.add(productId);
            else next.delete(productId);
            return next;
        });
    }

    function selectAll(checked) {
        if (!checked) {
            setSelectedIds(new Set());
            return;
        }
        setSelectedIds(new Set(items.map((item) => item.id)));
    }

    function moveProduct(from, to) {
        if (from === to || to < 0 || to >= items.length) return;
        if (bulkBusy) return;

        const previous = items;
        const next = reorderList(items, from, to);
        setItems(next);
        queueOrderSave(next, previous);
    }

    function onDragEnd(result) {
        const { source, destination } = result;
        if (!destination) return;
        if (source.index === destination.index) return;
        moveProduct(source.index, destination.index);
    }

    return (
        <ThemeProvider theme={blackIconTheme}>
            <div className={styles.page}>
                <header className={styles.header}>
                    <div className={styles.heroLeft}>
                        <div className={styles.logoWrap}>
                            {brand?.image ? (
                                <img src={toImgSrcKey(brand.image)} alt={brand?.name || 'Brand'} className={styles.logo} />
                            ) : (
                                <Avatar sx={{ bgcolor: '#111', color: '#fff' }}>
                                    {(brand?.name || '?')[0]}
                                </Avatar>
                            )}
                        </div>

                        <div>
                            <h2 className={styles.title}>{brand?.name || 'Brand'}</h2>
                            <div className={styles.metaRow}>
                                <Chip size="small" label={`${productCount} products`} />
                                <Chip size="small" label={autosaveLabel} />
                            </div>
                        </div>
                    </div>

                    <div className={styles.actions}>
                        <Button component={RouterLink} to="/brands" variant="outlined">Brands</Button>
                        <Button component={RouterLink} to={`/products/new?brandId=${encodeURIComponent(id)}`} variant="contained" color="inherit">
                            Add product
                        </Button>
                        <Button variant="outlined" color="error" onClick={onBulkDeleteSelected} disabled={!selectedCount || bulkBusy}>
                            Delete selected ({selectedCount})
                        </Button>
                        <Button variant="outlined" color="error" onClick={onDeleteBrand} disabled={productCount > 0}>
                            Delete brand
                        </Button>
                    </div>
                </header>

                {err && <Alert severity="error">{err}</Alert>}

                <section className={styles.gridSection}>
                    <Box className={styles.sectionHeader}>
                        <Typography variant="subtitle1" fontWeight={700}>Brand products</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Drag handle, move buttons, and Alt + Arrow are all supported.
                        </Typography>
                    </Box>

                    <BrandProductsGrid
                        items={items}
                        loading={loading}
                        savingOrder={autosave === 'saving'}
                        selectedIds={selectedIds}
                        onToggleSelect={toggleSelect}
                        onSelectAll={selectAll}
                        onDelete={onDeleteProduct}
                        onDragEnd={onDragEnd}
                        onMove={moveProduct}
                        brand={brand}
                    />
                </section>

                <Snackbar
                    open={notice.open}
                    autoHideDuration={3000}
                    onClose={() => setNotice((prev) => ({ ...prev, open: false }))}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                >
                    <Alert
                        onClose={() => setNotice((prev) => ({ ...prev, open: false }))}
                        severity={notice.severity}
                        sx={{ width: '100%' }}
                    >
                        {notice.message}
                    </Alert>
                </Snackbar>
            </div>
        </ThemeProvider>
    );
}
